import {
  AppState,
  Auth0Provider,
  AuthorizationParams,
} from "@auth0/auth0-react";
import { ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import { AUTH_CALLBACK_PATH } from "./auth-callback-route";
import { AuthErrorGate } from "./auth-error-gate";

/**
 * The Auth0 slice of an app's build-time config. Janus and Portus both expose
 * these keys on their own `config` object, so each can pass it straight in.
 */
export interface Auth0Config {
  AUTH0_DOMAIN: string;
  AUTH0_CLIENT_ID: string;
  AUTH0_AUDIENCE: string;
}

/**
 * Only a path inside this app, as Auth0 recommends for `onRedirectCallback`:
 * `//host` and absolute URLs would take the user to another site.
 */
const toSafeReturnTo = (returnTo: string | undefined) =>
  returnTo?.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/";

export const AuthProvider = ({
  config,
  children,
}: {
  config: Auth0Config;
  children?: ReactNode;
}) => {
  const navigate = useNavigate();

  // Replace, so Back never returns to the spent `code` and `state`.
  const onRedirectCallback = useCallback(
    (appState: AppState | undefined) => {
      void navigate(toSafeReturnTo(appState?.returnTo), { replace: true });
    },
    [navigate],
  );

  const authorizationParams: AuthorizationParams = useMemo(
    () => ({
      redirect_uri: `${window.location.origin}${AUTH_CALLBACK_PATH}`,
      audience: config.AUTH0_AUDIENCE,
    }),
    [config.AUTH0_AUDIENCE],
  );

  return (
    <Auth0Provider
      domain={config.AUTH0_DOMAIN}
      clientId={config.AUTH0_CLIENT_ID}
      authorizationParams={authorizationParams}
      onRedirectCallback={onRedirectCallback}
      // Token renewal must not depend on a hidden iframe against the Auth0
      // custom domain: wherever the app and Auth0 sit on different sites (the
      // `*.fly.dev` previews against auth.dev.punto.ai, or a legacy
      // puntoauto.com origin during the P-2682 move) the iframe is cross-site,
      // and Safari/Firefox drop the session cookie outright. Refresh tokens
      // keep renewal same-origin on every host. Requires "Allow Offline Access"
      // on the Auth0 API plus refresh token rotation on the application.
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={false}
    >
      {/* Above every route and both apps' guards, so a login that came back as
          an error has nothing left below it to retry the login (P-2726). */}
      <AuthErrorGate>{children}</AuthErrorGate>
    </Auth0Provider>
  );
};
